import { useEffect, useRef, useState } from "react";
import { activateTitleTranslation, bilingualTitleLines, desktopChromeBrowser, desktopChromeTranslator, refineTitleTranslation, titlePair, translateTitle, useTitleTranslationState } from "./titleTranslation";

export function BilingualTitle({ title, provided }: { title: string; provided?: string }) {
  const state = useTitleTranslationState();
  const target = useRef<HTMLSpanElement>(null);
  const [result, setResult] = useState<{ title: string; value: string } | null>(null);
  const pair = titlePair(title);
  useEffect(() => {
    if ((state !== "ready" && state !== "partial") || !pair || provided) return;
    let active = true;
    const element = target.current;
    if (!element) return;
    const run = () => { void translateTitle(title).then(value => { if (active && value) setResult({ title, value }); }); };
    if (!window.IntersectionObserver) run();
    else {
      const observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); run(); } }, { rootMargin: "120px" });
      observer.observe(element);
      return () => { active = false; observer.disconnect(); };
    }
    return () => { active = false; };
  }, [state, title, pair, provided]);
  const translated = provided && pair ? refineTitleTranslation(title, provided, pair) : result?.title === title ? result.value : null;
  const lines = bilingualTitleLines(title, translated, pair);
  return <><span ref={target} className="bilingual-primary" lang={lines.primaryLang}>{lines.primary}</span>{lines.secondary && <span className="bilingual-secondary" lang={lines.secondaryLang}>{lines.secondary}</span>}</>;
}

export function TitleTranslationControl() {
  const state = useTitleTranslationState();
  const autoAttempted = useRef(false);
  useEffect(() => {
    if (autoAttempted.current || !desktopChromeTranslator() || !["setup", "partial"].includes(state)) return;
    const activate = () => {
      autoAttempted.current = true;
      void activateTitleTranslation();
      document.removeEventListener("pointerdown", activate, true);
      document.removeEventListener("keydown", activate, true);
    };
    document.addEventListener("pointerdown", activate, true);
    document.addEventListener("keydown", activate, true);
    return () => { document.removeEventListener("pointerdown", activate, true); document.removeEventListener("keydown", activate, true); };
  }, [state]);
  if (state === "checking") return desktopChromeTranslator() ? <span className="bilingual-progress" role="status">准备本机翻译…</span> : null;
  if (state === "unavailable") return desktopChromeBrowser() ? <span className="bilingual-progress" role="status" title="此 Chrome 未提供中英语种的本机 Translator；请更新 Chrome 并在浏览器设置中允许使用本机 AI。">本机双语暂不可用</span> : null;
  if (state !== "setup" && state !== "partial" && state !== "error") return null;
  return <button className="button bilingual-enable" type="button" onClick={() => void activateTitleTranslation()} title="点击准备 Chrome 本机语言包；标题仅在此浏览器处理">
    {state === "error" ? "重试双语标题" : state === "partial" ? "补全双语标题" : "启用双语标题"}
  </button>;
}
